import React from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Badge as UiBadge } from '@/components/ui';

// Import các Component mới
import { SuperAdminKPIGrid } from './components/kpi/SuperAdminKPIGrid';
import { RevenueLineChart } from './components/charts/RevenueLineChart';
import { PlanRevenueDonut } from './components/charts/PlanRevenueDonut';
import { SystemActivityWidget } from './components/widgets/SystemActivityWidget';
import { RecentTenantsTable } from './components/tables/RecentTenantsTable';
import { PlanStatusTable } from './components/tables/PlanStatusTable';
import { SystemUsageWidget } from './components/widgets/SystemUsageWidget';
import { RecentLogsWidget } from './components/widgets/RecentLogsWidget';
import { RecentTicketsWidget } from './components/widgets/RecentTicketsWidget';
import { useSuperAdminDashboard } from './hooks/useSuperAdminDashboard';

export default function AdminDashboardPage() {
  const { data, isLoading, isError } = useSuperAdminDashboard();

  if (isLoading) {
    return (
      <PageLayout title="Super Admin" description="Đang tải dữ liệu trung tâm điều hành...">
        <div className="flex items-center justify-center h-64 text-emerald-600">
          <i className="ri-loader-4-line text-4xl animate-spin"></i>
        </div>
      </PageLayout>
    );
  }

  if (isError || !data) {
    return (
      <PageLayout title="Super Admin" description="Đã xảy ra lỗi khi tải dữ liệu.">
        <div className="p-4 bg-red-50 text-red-600 rounded-md flex flex-col items-start gap-4">
          <p>Không thể kết nối đến máy chủ dữ liệu (isError: {String(isError)}, hasData: {String(!!data)}).</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
          >
            Tải lại trang (Xóa cache)
          </button>
        </div>
      </PageLayout>
    );
  }
  return (
    <PageLayout
      title="Super Admin"
      description="Quản trị toàn hệ thống Landscape"
      badge={
        <UiBadge variant="custom" className="bg-slate-900 text-amber-400 border-amber-500/50 h-7 px-4 shadow-lg shadow-amber-500/10">
          <i className="ri-vip-crown-fill mr-1.5 sm:text-sm text-xs" />
          SYSTEM MASTER
        </UiBadge>
      }
      beforeToolbar={<SuperAdminKPIGrid kpis={data.kpis} />}
    >
      <div className="flex flex-col gap-6">
        {/* Tầng 1.5: Truy cập nhanh */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/companies" className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-md hover:border-emerald-500 hover:shadow-sm transition-all group">
            <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
              <i className="ri-building-2-line text-lg"></i>
            </div>
            <div>
              <p className="sm:text-sm text-xs font-medium text-slate-700 group-hover:text-emerald-700 transition-colors">Tổ chức</p>
              <p className="sm:text-sm text-xs text-slate-400">Quản lý khách hàng</p>
            </div>
          </Link>
          <Link to="/admin/users" className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-md hover:border-emerald-500 hover:shadow-sm transition-all group">
            <div className="w-10 h-10 rounded-md bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
              <i className="ri-user-settings-line text-lg"></i>
            </div>
            <div>
              <p className="sm:text-sm text-xs font-medium text-slate-700 group-hover:text-emerald-700 transition-colors">Người dùng</p>
              <p className="sm:text-sm text-xs text-slate-400">Phân quyền & Tài khoản</p>
            </div>
          </Link>
          <Link to="/admin/subscription" className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-md hover:border-emerald-500 hover:shadow-sm transition-all group">
            <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
              <i className="ri-vip-diamond-line text-lg"></i>
            </div>
            <div>
              <p className="sm:text-sm text-xs font-medium text-slate-700 group-hover:text-emerald-700 transition-colors">Gói dịch vụ</p>
              <p className="sm:text-sm text-xs text-slate-400">Quản lý nâng cấp</p>
            </div>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-md hover:border-emerald-500 hover:shadow-sm transition-all group">
            <div className="w-10 h-10 rounded-md bg-slate-50 text-slate-600 flex items-center justify-center group-hover:bg-slate-100 transition-colors">
              <i className="ri-settings-4-line text-lg"></i>
            </div>
            <div>
              <p className="sm:text-sm text-xs font-medium text-slate-700 group-hover:text-emerald-700 transition-colors">Cấu hình</p>
              <p className="sm:text-sm text-xs text-slate-400">Hệ thống cốt lõi</p>
            </div>
          </Link>
        </div>

        {/* Tầng 2: Biểu đồ & Hoạt động */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-6">
            <RevenueLineChart data={data.revenueChart} />
          </div>
          <div className="lg:col-span-3">
            <PlanRevenueDonut data={data.planRevenue} totalValue={data.kpis.totalRevenue} />
          </div>
          <div className="lg:col-span-3">
            <SystemActivityWidget activities={data.systemActivity} />
          </div>
        </div>

        {/* Tầng 3: Bảng Dữ liệu */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-6">
            <RecentTenantsTable tenants={data.recentTenants} />
          </div>
          <div className="lg:col-span-6">
            <PlanStatusTable plans={data.planRevenue} />
          </div>
        </div>

        {/* Tầng 4: Hệ thống */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-6">
            <SystemUsageWidget usage={data.usage} />
          </div>
          <div className="lg:col-span-3">
            <RecentLogsWidget logs={data.recentLogs} />
          </div>
          <div className="lg:col-span-3">
            <RecentTicketsWidget tickets={data.recentTickets} />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
